import type { CampaignDetail, DeliverableServingStatus } from './api';

// Spec da3515af — one derivation for "is this actually running", read by
// BOTH the campaign header pill and the ad card chip.
//
// It is one function on purpose. The bug this fixes was the header
// claiming LIVE from our own launch record; deriving the header and the
// chip separately would reproduce it at card scale, with a header and
// the card beneath it disagreeing. If you need the label somewhere new,
// call this — don't re-derive it.

/** How long a platform reading stays trustworthy.
 *
 *  Purpose: the refresh is best-effort and swallows its own errors, so
 *  without a cutoff a dead refresh would freeze the last reading forever
 *  and an ad approved hours ago would keep showing IN REVIEW. Past this
 *  window we stop asserting and fall back to the pre-existing label.
 *
 *  This was 6h, set against a comment claiming the refresh runs every 30
 *  minutes. It does not — that figure belonged to a DIFFERENT scheduler
 *  (conversion-upload-scheduler.ts). The real writer is
 *  launched-google-metrics-refresh.ts on the brand-coach tick, and two
 *  constants there set the true floor:
 *    MIN_REFRESH_INTERVAL_MS = 2h   — write side won't re-read sooner
 *    brand-coach cadence     = 1-4h — the tick that does the re-read
 *  So a settled reading can legitimately be ~6h old, and a cutoff AT the
 *  write interval made the trusted window ZERO-WIDTH: readings became
 *  untrusted at the same moment they became refreshable, and the pill
 *  reverted to the pre-fix LIVE for hours at a time on a perfectly
 *  healthy system.
 *
 *  The write gate dropped 6h -> 2h (decision 8e6b24e, after measuring
 *  what Google actually charges: one operation per Search regardless of
 *  rows, and no per-second limit on that service). That WIDENS this
 *  margin rather than narrowing it — the floor fell from ~10h to ~6h
 *  against an unchanged 12h cutoff — so 12h stays correct and is now
 *  more generous than it needs to be. Tightening it would make the pill
 *  fresher and is a deliberate separate change, not a consequence of
 *  this one; keep it strictly above write-interval + tick + headroom.
 *
 *  12h is deliberately generous in the honest direction: a campaign
 *  google just approved may show IN REVIEW until the next tick, which
 *  under-claims, whereas the failure this whole design exists to kill is
 *  over-claiming LIVE while nothing serves. Campaigns actually in review
 *  are exempt from the write side's gate entirely, so that lateness is
 *  one tick, not hours.
 *
 *  If you change either constant above, change this one. */
const STALE_AFTER_MS = 12 * 60 * 60 * 1000;

export type ServingState =
  /** Platform confirms it is serving. */
  | 'serving'
  /** Platform has it pending — most commonly ads under review. */
  | 'pending'
  /** Platform is serving it, but restricted (LIMITED / MISCONFIGURED). */
  | 'limited'
  /** No usable reading: never launched for real, no row yet, or stale.
   *  Callers must render this exactly as they did before serving state
   *  existed. */
  | 'unknown';

/** Map Google's CampaignPrimaryStatus onto the three states the founder
 *  cares about. Anything unrecognised is 'unknown' rather than a guess —
 *  Google extends this enum and a new value must not silently read as
 *  "serving". */
export function servingStateFromStatus(primaryStatus: string): ServingState {
  switch (primaryStatus) {
    case 'eligible':
    case 'learning':
      return 'serving';
    case 'pending':
      return 'pending';
    case 'limited':
    case 'misconfigured':
      return 'limited';
    // paused / removed / ended are already carried by our own campaign
    // status, and the founder sees them there. Treating them as a
    // serving signal here would double-report the same fact.
    default:
      return 'unknown';
  }
}

/** Is this reading recent enough to assert anything from? */
export function isFresh(reading: DeliverableServingStatus, now: number = Date.now()): boolean {
  const t = Date.parse(reading.fetchedAt);
  if (Number.isNaN(t)) return false;
  return now - t <= STALE_AFTER_MS;
}

/** The serving state of one launched deliverable.
 *
 *  Order matters. The mock check comes FIRST, before any platform
 *  reading is consulted: a mock-launched ad never reached Google, so any
 *  platform state attached to it would be fabricated. This mirrors the
 *  existing short-circuit in unlaunchedPaidAdCount. */
export function deliverableServingState(
  // Widened to `| null | undefined` deliberately: the plan-view
  // deliverable types declare these as `string | undefined`, and under
  // exactOptionalPropertyTypes a `| null`-only signature rejects them.
  deliverable: {
    launched_at?: string | null | undefined;
    launched_via?: string | null | undefined;
    id?: string | undefined;
  },
  servingStatus: Record<string, DeliverableServingStatus> | undefined,
  now: number = Date.now(),
): ServingState {
  if (!deliverable.launched_at) return 'unknown';
  if (deliverable.launched_via === 'mock') return 'unknown';
  const reading = deliverable.id ? servingStatus?.[deliverable.id] : undefined;
  if (!reading) return 'unknown';
  if (!isFresh(reading, now)) return 'unknown';
  return servingStateFromStatus(reading.primaryStatus);
}

/** The campaign-level state shown in the header pill: the least-serving
 *  state across its launched paid deliverables.
 *
 *  Least-serving wins because the founder's question is "is my money
 *  working", and one pending ad in a two-ad campaign means part of it
 *  is not. Reporting the best state would be the same species of
 *  optimism this whole spec exists to remove. */
export function campaignServingState(
  campaign: Pick<CampaignDetail, 'platformServingStatus'>,
  deliverables: Array<{
    launched_at?: string | null | undefined;
    launched_via?: string | null | undefined;
    id?: string | undefined;
  }>,
  now: number = Date.now(),
): ServingState {
  return leastServing(
    deliverables.map((d) => deliverableServingState(d, campaign.platformServingStatus, now)),
  );
}

/** Least-serving wins. Extracted so the detail page and the campaigns list
 *  cannot end up with two different opinions about precedence — the class of
 *  drift spec be02dc43 exists to stop. */
function leastServing(states: ServingState[]): ServingState {
  if (states.some((s) => s === 'pending')) return 'pending';
  if (states.some((s) => s === 'limited')) return 'limited';
  if (states.some((s) => s === 'serving')) return 'serving';
  return 'unknown';
}

/** Campaign-level serving state from the readings ALONE, for surfaces that
 *  carry no deliverable list — i.e. the campaigns list, whose payload has the
 *  status map but not the draft.
 *
 *  WHY DROPPING THE DELIVERABLE GUARD IS SAFE HERE. `deliverableServingState`
 *  refuses a deliverable that never launched or launched via 'mock', because a
 *  mock ad never reached Google and any platform state on it would be
 *  fabricated. That guard is redundant against this map: rows in
 *  google_paid_ad_metrics_history are written only from a real Google snapshot
 *  (campaigns.ts and ad-accounts.ts are the sole production writers), so a mock
 *  launch contributes no row and cannot appear here in the first place.
 *
 *  Everything that IS a rule — what each status means, and the 12h freshness
 *  window — is applied identically to the deliverable path, so the two
 *  surfaces agree about the chip even though the chip and the VERDICT may
 *  disagree by design (decision 909a74f). */
export function servingStateFromStatusMap(
  readings: Record<string, DeliverableServingStatus> | undefined,
  now: number = Date.now(),
): ServingState {
  if (!readings) return 'unknown';
  return leastServing(
    Object.values(readings).map((r) =>
      isFresh(r, now) ? servingStateFromStatus(r.primaryStatus) : 'unknown',
    ),
  );
}

/** Founder-facing reason text for a pending/limited reading.
 *
 *  Google's reason codes are SHOUTY_SNAKE_CASE enum values. Mag doesn't
 *  talk like that, so only the reasons worth naming get a phrase and
 *  everything else returns null — the caller then says something true
 *  but general rather than leaking MOST_ADS_UNDER_REVIEW at a founder. */
export function servingReasonPhrase(reasons: string[]): string | null {
  if (reasons.includes('MOST_ADS_UNDER_REVIEW') || reasons.includes('ADS_UNDER_REVIEW')) {
    return "google's still reading your ad";
  }
  if (reasons.includes('CAMPAIGN_PENDING')) return "it hasn't started yet";
  if (reasons.includes('BUDGET_CONSTRAINED') || reasons.includes('BUDGET_MISCONFIGURED')) {
    return "there's something off with the budget";
  }
  if (reasons.includes('BIDDING_STRATEGY_MISCONFIGURED')) {
    return "there's something off with the bidding";
  }
  if (reasons.includes('HAS_ADS_LIMITED_BY_POLICY')) {
    return 'google has limited some of your ads on policy grounds';
  }
  // CAMPAIGN_PAUSED is deliberately absent: our own campaign status already
  // carries it and the founder sees it there, so naming it here would report
  // the same fact twice.
  return null;
}
